import React from 'react';
import { motion } from 'framer-motion';
import { differentiators, valuePropositions } from '../data/mock';
import { Shield, GitBranch, Factory, FileText, Target, DollarSign, Clock, Award, ArrowRight, MapPin, Building2, Wrench, Layers, Box, CheckCircle } from 'lucide-react';

const iconMap = { Shield, GitBranch, Factory, FileText, Target, DollarSign, Clock, Award, MapPin, Building2, Wrench, Layers, Box };

const WhyTurnkeySection = () => {
  return (
    <section className="py-20 md:py-28 bg-[#FAFBFC]">
      <div className="max-w-6xl mx-auto px-5 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="badge-minimal mb-6">Why Choose Us</span>
          <h2 className="title-minimal mb-4">
            The <span className="title-accent">Turnkey</span> Advantage
          </h2>
          <p className="text-base text-[#4A4A5A] max-w-2xl mx-auto">
            Single-source responsibility means streamlined communication, reduced risk, 
            and faster project delivery.
          </p>
        </motion.div>

        {/* Value Props Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
          {valuePropositions.map((item, index) => {
            const IconComponent = iconMap[item.icon] || Shield;
            return (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="card-minimal p-6 hover:border-[#1C4B8A]/30"
              >
                <div className="w-11 h-11 rounded-lg bg-[#1C4B8A] flex items-center justify-center mb-4">
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-[#1A1A2E] mb-2">{item.title}</h3>
                <p className="text-sm text-[#4A4A5A] leading-relaxed">{item.description}</p>
              </motion.div>
            );
          })}
        </div>

        {/* Differentiators */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-semibold text-[#1A1A2E] mb-8">What Sets Us Apart</h3>
            <div className="space-y-3">
              {differentiators.map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="flex gap-4 p-4 rounded-lg bg-white border border-[#E5E7EB] hover:border-[#1C4B8A]/30 transition-colors"
                >
                  <div className="w-9 h-9 rounded-lg bg-[#1C4B8A]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-[#1C4B8A]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#1A1A2E] mb-1">{item.title}</h4>
                    <p className="text-sm text-[#4A4A5A]">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="bg-[#1C4B8A] rounded-2xl p-10 text-white"
          >
            <div className="text-6xl font-bold mb-2">30+</div>
            <div className="text-xl font-semibold mb-3">Years of Excellence</div>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Since 1989, we've been Canada's trusted partner for cleanroom solutions, 
              delivering projects from design through certification.
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 text-white font-medium hover:gap-3 transition-all"
            >
              Start Your Project <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyTurnkeySection;
